import React from "react";
import {
  Box,
  Button,
  Typography,
  Paper,
  Grid,
  Avatar,
  Chip,
  Divider,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import FlagIcon from "@mui/icons-material/Flag";
import EmailIcon from "@mui/icons-material/Email";
import VerifiedIcon from "@mui/icons-material/Verified";
import PersonIcon from "@mui/icons-material/Person";
import PublicIcon from "@mui/icons-material/Public";
import ImageLoader from "../ImageLoader";

const MainProfileSection = ({ user, profile, loading, handleStatusChange }) => {
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      return new Date(dateString).toLocaleString();
    } catch {
      return "Invalid Date";
    }
  };

  const renderStatusButton = () => {
    const isActive = user.status === "active";
    const color = isActive ? "error" : "success";
    const loadingText = isActive ? "Blocking..." : "Unblocking...";
    const buttonText = isActive ? "Block Account" : "Unblock Account";

    return (
      <Button
        variant="contained"
        color={color}
        onClick={() => handleStatusChange(isActive ? "blocked" : "active")}
        disabled={loading}
        startIcon={loading ? <CircularProgress size={20} /> : null}
        sx={{
          minWidth: 160,
          fontWeight: 600,
          textTransform: "none",
          borderRadius: 2,
          px: 3,
        }}
      >
        {loading ? loadingText : buttonText}
      </Button>
    );
  };

  const meta = profile?.meta || {};
  const location = user?.location || {};
  const countryFlag = meta?.country?.flag || profile?.country?.flag;

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        mb: 4,
        borderRadius: 3,
        backgroundColor: "background.paper",
        border: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <Grid container spacing={4}>
        {/* LEFT COLUMN — Profile Image & Status */}
        <Grid item xs={12} md={4}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
              position: "sticky",
              top: 20,
            }}
          >
            {/* Profile Picture */}
            {profile.profilePicture?.url ? (
              <ImageLoader
                source={{ uri: profile.profilePicture.url }}
                alt={profile.fullname}
                style={{
                  width: 160,
                  height: 160,
                  borderRadius: "50%",
                  objectFit: "cover",
                  objectPosition: "top", // <-- centers face/head
                  marginBottom: 16,
                  border: "2px solid rgba(199, 164, 93, 0.6)",
                }}
              />
            ) : (
              <Avatar
                sx={{
                  width: 150,
                  height: 150,
                  fontSize: 60,
                  bgcolor: "primary.main",
                  color: "black",
                }}
              >
                {user.fullname?.charAt(0)?.toUpperCase() || "U"}
              </Avatar>
            )}

            {/* Status Chip */}
            <Chip
              label={user.status?.toUpperCase() || "UNKNOWN"}
              color={
                user.status === "active"
                  ? "success"
                  : user.status === "blocked"
                  ? "error"
                  : "warning"
              }
              sx={{ fontWeight: "bold", letterSpacing: 0.5 }}
            />

            {/* Action Button */}
            <Box>{renderStatusButton()}</Box>

            {/* Account Type */}
            <Typography
              variant="body2"
              color="text.secondary"
              px={0}
              pt={1}
              m={0}
            >
              Role: {user.role || "User"} | Tier: {user.tier || "Free"}
            </Typography>

            {/* Country */}
            {countryFlag && (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                }}
              >
                <img
                  src={countryFlag}
                  alt={meta?.country?.name || "Country"}
                  width={24}
                  height={16}
                  style={{ borderRadius: 3 }}
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  px={0}
                  py={0}
                  m={0}
                >
                  {meta?.country?.name || "N/A"}
                </Typography>
              </Box>
            )}
          </Box>
        </Grid>

        {/* RIGHT COLUMN — Details */}
        <Grid item xs={12} md={8}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {user.fullname ||
              `${profile?.name?.firstName || ""} ${
                profile?.name?.lastName || ""
              }`}
          </Typography>
          <Typography variant="subtitle1" color="text.secondary" gutterBottom>
            @{user.username} • {user.email}
          </Typography>

          <Divider sx={{ my: 2, borderColor: "rgba(199, 164, 93, 0.2)" }} />

          {/* Account Info */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 1, color: "primary.main" }}
            px={0}
            pb={1}
            m={0}
          >
            Account Details
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography px={0} py={1} m={0}>
                <strong>Email Verified:</strong>{" "}
                {user.emailVerified ? "Yes" : "No"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Profile Completed:</strong>{" "}
                {user.completion || profile.completed ? "Yes" : "No"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Verified Profile:</strong>{" "}
                {profile.isVerified ? "Yes" : "No"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Created At:</strong> {formatDate(user.createdAt)}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Last Seen:</strong> {formatDate(user.lastSeen)}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography px={0} py={1} m={0}>
                <strong>Last Seen Message:</strong>{" "}
                {user.lastSeenMessage || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>IP Address:</strong> {location.ip || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>GeoLocation:</strong> {location.geoLocation || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Marked Deleted:</strong>{" "}
                {user.markedDeleted ? "Yes" : "No"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Email Sent:</strong> {user.emailSent ? "Yes" : "No"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: "rgba(199, 164, 93, 0.1)" }} />

          {/* Personal Info */}
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", mb: 1, color: "primary.main" }}
            px={0}
            pb={1}
            m={0}
          >
            Personal & Religious Info
          </Typography>
          <Grid container spacing={1}>
            <Grid item xs={6}>
              <Typography px={0} py={1} m={0}>
                <strong>Gender:</strong> {meta.gender || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Age:</strong>{" "}
                {meta.dob
                  ? `${
                      new Date().getFullYear() -
                      new Date(meta.dob).getFullYear()
                    } yrs`
                  : "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Marital Status:</strong> {meta.maritalStatus || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Sect:</strong> {meta.sect || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>How Religious:</strong> {meta.howReligious || "N/A"}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography px={0} py={1} m={0}>
                <strong>Prays:</strong> {meta.prays || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Fasts:</strong> {meta.fasts || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Eats Halal:</strong> {meta.eatsHalal || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Convert/Revert:</strong> {meta.convertOrRevert || "N/A"}
              </Typography>
              <Typography px={0} py={1} m={0}>
                <strong>Profile Creator:</strong> {meta.profileCreator || "N/A"}
              </Typography>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3, borderColor: "rgba(199, 164, 93, 0.1)" }} />

          {/* Bio */}
          {meta.bio && (
            <Box>
              <Typography
                variant="h6"
                sx={{ fontWeight: "bold", mb: 1, color: "primary.main" }}
                 px={0} pb={1} m={0}
              >
                About Me
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "pre-line",
                  color: "text.primary",
                  lineHeight: 1.6,
                }}
                 px={0} py={1} m={0}
              >
                {meta.bio}
              </Typography>
            </Box>
          )}
        </Grid>
      </Grid>
    </Paper>
  );
};

export default MainProfileSection;
